// ---------------------------------------------------------------------------
// DealTeam.js — Manage deal team members (names, roles, default split %)
// ---------------------------------------------------------------------------
import React, { useEffect, useState, useCallback } from "react";
import api from "../api/client";
import DealTeamPromote from "../components/DealTeamPromote";

const C = {
  bg: "#fbf9f6",
  card: "#fff",
  border: "#e0dbd5",
  text: "#333332",
  muted: "#6b6b6b",
  accent: "#345479",
  green: "#2d6a4f",
  red: "#9b2226",
};

const ROLE_OPTIONS = [
  { value: "Employee", label: "Employee" },
  { value: "ManagementCompany", label: "Management Company" },
  { value: "Advisor", label: "Advisor" },
  { value: "Other", label: "Other" },
];

const inputS = { width: "100%", padding: "7px 10px", borderRadius: 6, border: `1px solid #d1d5db`, fontSize: 13, boxSizing: "border-box", background: "#fff" };
const btn = { fontSize: 12, padding: "6px 14px", borderRadius: 4, border: "none", cursor: "pointer", fontWeight: 600 };

export default function DealTeam() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ member_name: "", role: "Employee", default_split_pct: "", is_active: true });
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/securities/deal-team/");
      setMembers(r.data.results || r.data || []);
    } catch (err) {
      showToast("Failed to load deal team members", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startAdd = () => {
    setEditingId(null);
    setDraft({ member_name: "", role: "Employee", default_split_pct: "", is_active: true });
    setShowForm(true);
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setDraft({
      member_name: m.member_name || "",
      role: m.role || "Employee",
      default_split_pct: m.default_split_pct != null ? String((parseFloat(m.default_split_pct) * 100).toFixed(4)).replace(/\.?0+$/, "") : "",
      is_active: m.is_active !== false,
    });
    setShowForm(true);
  };

  const cancel = () => { setShowForm(false); setEditingId(null); };

  const save = async () => {
    if (!draft.member_name.trim()) { showToast("Name is required", "error"); return; }
    setSaving(true);
    try {
      const pct = parseFloat(draft.default_split_pct);
      const payload = {
        member_name: draft.member_name.trim(),
        role: draft.role || "",
        is_active: !!draft.is_active,
      };
      if (!isNaN(pct) && pct > 0) payload.default_split_pct = (pct / 100).toFixed(6);
      if (editingId) {
        await api.patch(`/securities/deal-team/${editingId}/`, payload);
        showToast("Updated");
      } else {
        await api.post("/securities/deal-team/", payload);
        showToast("Added");
      }
      setShowForm(false);
      setEditingId(null);
      load();
    } catch (err) {
      const d = err.response?.data;
      showToast(typeof d === "object" ? JSON.stringify(d) : (d || err.message), "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.member_name}? Existing deal splits referencing this name will keep their text value but you won't be able to select this person for future deals.`)) return;
    try {
      await api.delete(`/securities/deal-team/${m.id}/`);
      showToast("Removed");
      load();
    } catch (err) {
      const d = err.response?.data;
      showToast(typeof d === "object" ? JSON.stringify(d) : (d || err.message), "error");
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/securities/deal-team/${m.id}/`, { is_active: !m.is_active });
      load();
    } catch (err) {
      showToast("Toggle failed", "error");
    }
  };

  return (
    <div style={{ padding: "0 24px 40px", background: C.bg, minHeight: "100vh" }}>
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 2000, padding: "12px 20px",
          borderRadius: 8, background: toast.type === "error" ? C.red : C.green,
          color: "#fff", fontSize: 14, fontWeight: 600, boxShadow: "0 8px 30px rgba(0,0,0,.25)",
        }}>
          {toast.msg}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "24px 0 12px" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>Deal Team</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: "4px 0 0" }}>
            Promote performance, deal contributions and the people who share in the team pool.
          </p>
        </div>
        {!showForm && (
          <button type="button" onClick={startAdd} style={{ ...btn, background: C.accent, color: "#fff" }}>
            + Add Team Member
          </button>
        )}
      </div>

      <DealTeamPromote />

      <h2 style={{ fontSize: 18, color: C.text }}>Team directory</h2>
      <p style={{ fontSize: 13, color: C.muted }}>Default splits are used when setting up deals. The promote report uses each deal’s recorded terms.</p>
      {showForm && (
        <div style={{ background: C.card, borderRadius: 10, border: `1px solid ${C.border}`, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 12 }}>
            {editingId ? "Edit team member" : "New team member"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 200px 140px 120px", gap: 12, alignItems: "end" }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 3 }}>Full name *</label>
              <input value={draft.member_name}
                onChange={e => setDraft(d => ({ ...d, member_name: e.target.value }))}
                placeholder="e.g. Jeremy Weech" style={inputS} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 3 }}>Role</label>
              <select value={draft.role}
                onChange={e => setDraft(d => ({ ...d, role: e.target.value }))}
                style={inputS}>
                {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 3 }}>Default split %</label>
              <input value={draft.default_split_pct}
                onChange={e => setDraft(d => ({ ...d, default_split_pct: e.target.value }))}
                placeholder="e.g. 33.33" type="text" inputMode="decimal"
                style={{ ...inputS, textAlign: "right" }} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.text, paddingBottom: 8 }}>
              <input type="checkbox" checked={draft.is_active}
                onChange={e => setDraft(d => ({ ...d, is_active: e.target.checked }))} />
              Active
            </label>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button type="button" onClick={save} disabled={saving || !draft.member_name.trim()}
              style={{ ...btn, background: saving || !draft.member_name.trim() ? "#9ca3af" : C.green, color: "#fff", cursor: saving || !draft.member_name.trim() ? "default" : "pointer" }}>
              {saving ? "Saving..." : editingId ? "Save changes" : "Add member"}
            </button>
            <button type="button" onClick={cancel}
              style={{ ...btn, background: "transparent", color: C.muted, border: `1px solid ${C.border}` }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ background: C.card, borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 140px 100px 160px", padding: "12px 20px", background: "#f7f5f1",
                      fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#475569" }}>
          <div>Name</div><div>Role</div><div style={{ textAlign: "right" }}>Default split</div><div>Status</div><div style={{ textAlign: "right" }}>Actions</div>
        </div>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: C.muted }}>Loading…</div>
        ) : members.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: C.muted }}>
            No deal team members yet. Click <strong>+ Add Team Member</strong> to create one.
          </div>
        ) : members.map(m => (
          <div key={m.id} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 140px 100px 160px", padding: "12px 20px",
                                  borderTop: `1px solid ${C.border}`, alignItems: "center", fontSize: 13, color: C.text,
                                  opacity: m.is_active === false ? 0.55 : 1 }}>
            <div style={{ fontWeight: 600 }}>{m.member_name}</div>
            <div>{m.role || <span style={{ color: C.muted }}>—</span>}</div>
            <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {m.default_split_pct != null ? `${(parseFloat(m.default_split_pct) * 100).toFixed(2)}%` : "—"}
            </div>
            <div>
              <button type="button" onClick={() => toggleActive(m)}
                style={{ ...btn, padding: "3px 10px", fontSize: 11,
                         background: m.is_active === false ? "#f1f5f9" : "#dcfce7",
                         color: m.is_active === false ? C.muted : C.green,
                         border: `1px solid ${m.is_active === false ? "#cbd5e1" : "#bbf7d0"}` }}>
                {m.is_active === false ? "Inactive" : "Active"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button type="button" onClick={() => startEdit(m)}
                style={{ ...btn, padding: "4px 12px", fontSize: 12, background: "transparent", color: C.accent, border: `1px solid ${C.accent}` }}>
                Edit
              </button>
              <button type="button" onClick={() => remove(m)}
                style={{ ...btn, padding: "4px 12px", fontSize: 12, background: "transparent", color: C.red, border: `1px solid ${C.red}` }}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
